import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AuthorizeSavedCardRequest,
  CardRegisterCreateRequest,
  CustomerCard,
  SavedCardApiEnv,
  SavedCardService,
} from 'src/app/services/saved-card.service';

type AppScreen = 'home' | 'cards' | 'waiting' | 'result';

type LatencyPhase = 'consult' | 'payment';

interface LatencyEntry {
  label: string;
  atMs: number;
  stepMs: number;
  cumulativeMs: number;
  phase: LatencyPhase;
}

interface PaymentResult {
  success: boolean;
  message: string;
  code?: string;
}

@Component({ standalone: false,
  selector: 'app-form-saved-card',
  templateUrl: './form-saved-card.component.html',
  styleUrls: ['./form-saved-card.component.css'],
})
export class FormSavedCardComponent implements OnInit, OnDestroy {
  myForm: FormGroup;
  appScreen: AppScreen = 'home';
  cards: CustomerCard[] = [];
  selectedCardToken = '';
  selectedCard: CustomerCard | null = null;

  isLoading = false;
  errorMessage = '';

  showRegisterIframe = false;
  registerIframeUrl = '';

  showAuthOverlay = false;
  authIframeUrl = '';
  authIframeVisible = false;
  waitingMessage = 'Procesando pago...';
  showWaitingSpinner = true;

  paymentResult: PaymentResult | null = null;
  latencyEntries: LatencyEntry[] = [];

  private postMessageListener: ((event: MessageEvent) => void) | null = null;
  private flowStartMs = 0;
  private paymentStartMs = 0;
  private authorizeResponseMs = 0;
  private orderIdInterval: ReturnType<typeof setInterval> | null = null;
  private lastLatencyAtMs = 0;
  private consultCumulativeMs = 0;
  private paymentCumulativeMs = 0;
  private authIframeLoadRecorded = false;
  private authChallengeSeen = false;
  private authChallengeSent = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly savedCardService: SavedCardService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.myForm = this.formBuilder.group({
      apiEnvironment: ['QA' as SavedCardApiEnv],
      apiKey: ['OCC-40467ba5-f254-4cf0-937e-9f42a206cb3f', Validators.required],
      secret: ['93ffbb5b3e574d76a89b43ded9daa0e4', Validators.required],
      orderNumber: [this.savedCardService.createOrderNumber(), Validators.required],
      customerIdentifier: ['sergio4421', Validators.required],
      name: ['Jhony', Validators.required],
      lastName: ['Pacheco', Validators.required],
      phone: ['900066880', Validators.required],
      email: ['test222@gmail.com', Validators.required],
      documentType: ['DNI'],
      documentNumber: ['42424242', Validators.required],
      addressCountry: ['PER'],
      addressLevel1: ['LIMA'],
      addressLevel2: ['LIMA'],
      addressLevel3: ['SAN ISIDRO'],
      addressLine1: ['Av Zeta 1'],
      addressZip: ['15036'],
      currencyCode: ['PEN'],
      countryCode: ['PER'],
      language: ['es_PE'],
      paymentMethod: ['Tarjetas-Credito-Debito-PROM'],
      amount: [10, [Validators.required, Validators.min(1)]],
      installment: ['3'],
      deferredInstallment: ['3'],
      productName: ['Heladera'],
      productSku: ['1234567890'],
      productCategory: ['Electrodomestico'],
    });
  }

  ngOnInit(): void {
    this.postMessageListener = (event: MessageEvent) => this.handlePostMessage(event);
    window.addEventListener('message', this.postMessageListener);
    this.startOrderIdInterval();
  }

  ngOnDestroy(): void {
    if (this.postMessageListener) {
      window.removeEventListener('message', this.postMessageListener);
    }
    this.stopOrderIdInterval();
  }

  get selectedApiEnv(): SavedCardApiEnv {
    return this.myForm.get('apiEnvironment')?.value ?? 'QA';
  }

  get selectedApiBaseUrl(): string {
    return this.savedCardService.apiBaseUrl(this.selectedApiEnv);
  }

  get selectedCardLabel(): string {
    if (!this.selectedCard) {
      return 'Ninguna tarjeta seleccionada';
    }
    const alias = this.selectedCard.alias ? ` (${this.selectedCard.alias})` : '';
    return `${this.selectedCard.brand} ${this.selectedCard.masked}${alias}`;
  }

  getCardBrandClass(brand: string): string {
    const normalized = (brand || '').toLowerCase();
    if (normalized.includes('visa')) return 'brand-visa';
    if (normalized.includes('master')) return 'brand-mastercard';
    if (normalized.includes('amex')) return 'brand-amex';
    return 'brand-default';
  }

  get currentTimeLabel(): string {
    const now = new Date();
    return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
  }

  get consultTimelineEntries(): LatencyEntry[] {
    return this.latencyEntries.filter((e) => e.phase === 'consult');
  }

  get paymentTimelineEntries(): LatencyEntry[] {
    return this.latencyEntries.filter((e) => e.phase === 'payment');
  }

  get totalConsultMs(): number {
    return this.sumStepMs(this.consultTimelineEntries);
  }

  get totalPaymentMs(): number {
    return this.sumStepMs(this.paymentTimelineEntries);
  }

  get totalFlowMs(): number {
    return this.totalConsultMs + this.totalPaymentMs;
  }

  get hasPaymentPhase(): boolean {
    return this.paymentTimelineEntries.length > 0;
  }

  get hasConsultPhase(): boolean {
    return this.consultTimelineEntries.length > 0;
  }

  formatDuration(ms: number): string {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(2)} s`;
    }
    return `${ms} ms`;
  }

  private sumStepMs(entries: LatencyEntry[]): number {
    return entries.reduce((sum, entry) => sum + entry.stepMs, 0);
  }

  startCardRegister(): void {
    if (this.myForm.invalid) {
      this.errorMessage = 'Completa los campos requeridos.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const form = this.myForm.value;
    const orderNumber = this.savedCardService.createOrderNumber();
    this.myForm.get('orderNumber')?.setValue(orderNumber, { emitEvent: false });
    const timestamp = this.savedCardService.createTimestamp();

    const body: CardRegisterCreateRequest = {
      order: {
        multipleCustomerIndicator: false,
        currency: { code: form.currencyCode },
        number: orderNumber,
        type: 'ORDER',
        cardRegister: true,
        integrationMode: 1,
        country: { code: form.countryCode },
        customer: this.buildCustomerPayload(),
        metadata: [],
      },
      settings: {
        expiration: {
          type: 'DATE',
          date: '2029-12-31T23:59:59.999-0500',
        },
        language: form.language,
      },
    };

    this.savedCardService
      .createCardRegisterOrder(body, form.apiKey, form.secret, orderNumber, timestamp, form.apiEnvironment)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          const link = response?.data?.order?.link;
          if (!link) {
            this.errorMessage = 'No se recibió link de registro.';
            return;
          }
          this.registerIframeUrl = link;
          this.showRegisterIframe = true;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage =
            error?.error?.message?.text || error?.error?.message || 'Error al crear orden de registro.';
        },
      });
  }

  skipRegisterAndGoToCards(): void {
    this.appScreen = 'cards';
  }

  consultCards(): void {
    if (this.myForm.invalid) {
      this.errorMessage = 'Completa los campos requeridos.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.stopOrderIdInterval();
    const form = this.myForm.value;
    const timestamp = this.savedCardService.createTimestamp();
    const consultStart = performance.now();

    if (!this.flowStartMs) {
      this.beginConsultTimeline();
    }

    this.savedCardService
      .getCustomerCards(
        form.customerIdentifier,
        {
          documentType: form.documentType,
          documentNumber: form.documentNumber,
          paymentMethod: form.paymentMethod,
        },
        form.apiKey,
        form.secret,
        timestamp,
        form.apiEnvironment,
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.markLatency(
            `HTTP consulta tarjetas (#${this.consultTimelineEntries.filter((e) => e.label.startsWith('HTTP consulta tarjetas')).length + 1})`,
            consultStart,
            'consult',
          );
          this.cards = response?.data ?? [];
          if (!this.cards.length) {
            this.errorMessage = 'No se encontraron tarjetas para este cliente.';
          }
          if (this.selectedCardToken && !this.cards.some((c) => c.token === this.selectedCardToken)) {
            this.selectedCardToken = '';
            this.selectedCard = null;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (this.flowStartMs) {
            this.markLatency('HTTP consulta tarjetas (error)', consultStart, 'consult');
          }
          this.errorMessage =
            error?.error?.message?.text || error?.error?.message || 'Error al consultar tarjetas.';
        },
      });
  }

  selectCard(card: CustomerCard): void {
    this.selectedCardToken = card.token;
    this.selectedCard = card;
  }

  payWithSelectedCard(): void {
    if (!this.selectedCardToken) {
      this.errorMessage = 'Selecciona una tarjeta antes de pagar.';
      return;
    }
    if (this.myForm.invalid) {
      this.errorMessage = 'Completa los campos requeridos.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.paymentResult = null;
    this.stopOrderIdInterval();
    this.paymentStartMs = performance.now();

    if (!this.flowStartMs) {
      this.beginPaymentOnlyTimeline();
    } else {
      this.beginPaymentPhase();
    }

    const form = this.myForm.value;
    const orderNumber = this.savedCardService.createOrderNumber();
    this.myForm.get('orderNumber')?.setValue(orderNumber, { emitEvent: false });
    const amount = Number(form.amount);
    const authorizeStart = performance.now();

    const body: AuthorizeSavedCardRequest = {
      order: {
        number: orderNumber,
        amount: Number(form.amount),
        discounts: 0,
        taxes: 0,
        fee: 0,
        shippingAmount: 0,
        country: { code: form.countryCode },
        currency: { code: form.currencyCode },
        products: [
          {
            name: form.productName,
            quantity: 1,
            unitAmount: Number(form.amount),
            amount: Number(form.amount),
            sku: form.productSku,
            category: form.productCategory,
            discount: 0,
            shipping: 0,
          },
        ],
        customer: this.buildCustomerPayload(),
        mode: 'STANDAR',
      },
      payment: {
        token: { identifier: this.selectedCardToken },
        installment: {
          installment: String(form.installment),
          deferredInstallment: String(form.deferredInstallment),
        },
        paymentMethod: form.paymentMethod,
        clientRequest: {
          browserParameters: this.savedCardService.buildBrowserParameters(),
        },
      },
      settings: {
        expiration: null,
        language: form.language,
      },
    };

    this.savedCardService
      .authorizeSavedCard(body, form.apiKey, form.secret, orderNumber, form.currencyCode, amount, form.apiEnvironment)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.authorizeResponseMs = performance.now();
          this.markLatency('HTTP autorizar saved-card (REVIEW)', authorizeStart, 'payment');

          const purchaseValidationUrl =
            response?.data?.processor?.payment?.authentication?.purchaseValidationUrl;

          if (!purchaseValidationUrl) {
            this.errorMessage = 'No se recibió purchaseValidationUrl en la respuesta.';
            this.showResult({
              success: false,
              message: 'Respuesta sin URL de autenticación.',
              code: response?.data?.code,
            });
            return;
          }

          this.openAuthWebview(purchaseValidationUrl);
        },
        error: (error) => {
          this.isLoading = false;
          this.markLatency('HTTP autorizar saved-card (error)', authorizeStart, 'payment');
          this.errorMessage =
            error?.error?.message?.text || error?.error?.message || 'Error al autorizar con tarjeta guardada.';
          this.showResult({
            success: false,
            message: this.errorMessage,
          });
        },
      });
  }

  closeRegisterIframe(): void {
    this.showRegisterIframe = false;
    this.registerIframeUrl = '';
    this.appScreen = 'cards';
  }

  onRegisterIframeLoad(): void {
    // Registro opcional fuera del timeline principal
  }

  onAuthIframeLoad(): void {
    if (this.authIframeLoadRecorded) {
      return;
    }
    this.authIframeLoadRecorded = true;
    this.markLatency('Iframe auth cargado', this.lastLatencyAtMs || this.authorizeResponseMs || this.paymentStartMs, 'payment');
  }

  resetFlow(): void {
    this.appScreen = 'home';
    this.cards = [];
    this.selectedCard = null;
    this.selectedCardToken = '';
    this.paymentResult = null;
    this.errorMessage = '';
    this.showAuthOverlay = false;
    this.authIframeUrl = '';
    this.authIframeVisible = false;
    this.showWaitingSpinner = true;
    this.waitingMessage = 'Procesando pago...';
    this.resetLatency();
    this.flowStartMs = 0;
    this.paymentStartMs = 0;
    this.authorizeResponseMs = 0;
    this.lastLatencyAtMs = 0;
    this.consultCumulativeMs = 0;
    this.paymentCumulativeMs = 0;
    this.authIframeLoadRecorded = false;
    this.authChallengeSeen = false;
    this.authChallengeSent = false;
    this.myForm.get('orderNumber')?.setValue(this.savedCardService.createOrderNumber(), { emitEvent: false });
    this.startOrderIdInterval();
  }

  private openAuthWebview(url: string): void {
    this.appScreen = 'waiting';
    this.showAuthOverlay = true;
    this.authIframeUrl = url;
    this.authIframeVisible = false;
    this.authIframeLoadRecorded = false;
    this.authChallengeSeen = false;
    this.authChallengeSent = false;
    this.showWaitingSpinner = true;
    this.waitingMessage = 'Preparando autenticación...';
    this.markLatency('Webview auth invisible abierta', this.lastLatencyAtMs || this.authorizeResponseMs || this.paymentStartMs, 'payment');
  }

  private handlePostMessage(event: MessageEvent): void {
    if (!this.isTrustedPayLinkOrigin(event.origin)) {
      return;
    }

    const payload = this.normalizePayload(event.data);
    if (!payload) {
      return;
    }

    console.log('[saved-card] postMessage:', payload);

    if (this.showRegisterIframe) {
      this.handleRegisterMessage(payload);
      return;
    }

    if (this.showAuthOverlay) {
      this.handleAuthMessage(payload);
    }
  }

  private handleRegisterMessage(payload: Record<string, unknown>): void {
    const type = String(payload['type'] ?? '');
    if (
      type === 'epago.payment-completed' ||
      type === 'EPAGO_PAYMENT_COMPLETED' ||
      type === 'PAYLINK_SUCCESS_MESSAGE'
    ) {
      setTimeout(() => this.closeRegisterIframe(), 1500);
    }
  }

  private handleAuthMessage(payload: Record<string, unknown>): void {
    const type = String(payload['type'] ?? '');

    switch (type) {
      case 'epago.payment-starting':
        this.waitingMessage = 'Iniciando autorización';
        this.showWaitingSpinner = true;
        this.markLatency('Webview auth lista (epago.payment-starting)', undefined, 'payment');
        break;
      case 'epago.authorize-init':
        this.waitingMessage = 'Autenticando tu orden';
        this.showWaitingSpinner = true;
        this.markLatency('Preparación auth (→ epago.authorize-init)', undefined, 'payment');
        break;
      case 'epago.challenge-starting':
        this.authChallengeSeen = true;
        this.showWaitingSpinner = false;
        this.authIframeVisible = true;
        this.waitingMessage = 'Completa la validación del banco';
        break;
      case 'epago.challenge-send':
        this.authChallengeSent = true;
        this.waitingMessage = 'Finalizando';
        this.showWaitingSpinner = true;
        break;
      case 'epago.payment-authentication-completed': {
        const success = payload['success'] === true;
        const response = payload['response'] as Record<string, unknown> | undefined;
        const data = response?.['data'] as Record<string, unknown> | undefined;
        const code = String(data?.['code'] ?? response?.['message'] ?? '');
        const text = String(
          (response?.['message'] as Record<string, unknown> | undefined)?.['text'] ??
            payload['message'] ??
            (success ? 'Pago autorizado' : 'Pago rechazado'),
        );
        this.markLatency(this.getAuthenticationCompletedLatencyLabel(), undefined, 'payment');
        this.finishAuthFlow(success, text, code);
        break;
      }
      default:
        break;
    }
  }

  private finishAuthFlow(success: boolean, message: string, code?: string): void {
    this.showAuthOverlay = false;
    this.authIframeUrl = '';
    this.authIframeVisible = false;
    this.showWaitingSpinner = true;
    this.waitingMessage = 'Procesando pago...';
    this.showResult({ success, message, code });
  }

  private showResult(result: PaymentResult): void {
    this.appScreen = 'result';
    this.paymentResult = result;
  }

  private buildCustomerPayload(): AuthorizeSavedCardRequest['order']['customer'] {
    const form = this.myForm.value;
    return {
      identifier: form.customerIdentifier,
      name: form.name,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      address: {
        country: form.addressCountry,
        levels: [form.addressLevel1, form.addressLevel2, form.addressLevel3],
        line1: form.addressLine1,
        zip: form.addressZip,
      },
      document: {
        type: form.documentType,
        number: form.documentNumber,
      },
    };
  }

  private normalizePayload(data: unknown): Record<string, unknown> | null {
    if (!data) {
      return null;
    }
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return typeof parsed === 'object' && parsed ? (parsed as Record<string, unknown>) : null;
      } catch {
        return null;
      }
    }
    if (typeof data === 'object') {
      return data as Record<string, unknown>;
    }
    return null;
  }

  private isTrustedPayLinkOrigin(origin: string): boolean {
    return !!origin && origin.includes('pay.link.epago.pe');
  }

  private getAuthenticationCompletedLatencyLabel(): string {
    if (this.authChallengeSent) {
      return 'POST endAuthentication (2s + API → completado)';
    }
    if (this.authChallengeSeen) {
      return 'POST endAuthentication tras challenge (→ completado)';
    }
    return 'POST startAuthentication (sin fricción → completado)';
  }

  private beginConsultTimeline(): void {
    this.resetLatency();
    this.flowStartMs = performance.now();
    this.lastLatencyAtMs = this.flowStartMs;
    this.consultCumulativeMs = 0;
    this.paymentCumulativeMs = 0;
  }

  private beginPaymentOnlyTimeline(): void {
    this.resetLatency();
    this.flowStartMs = performance.now();
    this.paymentStartMs = this.flowStartMs;
    this.lastLatencyAtMs = this.flowStartMs;
    this.consultCumulativeMs = 0;
    this.paymentCumulativeMs = 0;
  }

  private beginPaymentPhase(): void {
    this.paymentCumulativeMs = 0;
    this.lastLatencyAtMs = this.paymentStartMs;
  }

  private markLatency(label: string, fromMs?: number, phase: LatencyPhase = 'consult'): void {
    const atMs = performance.now();
    const stepBase = fromMs ?? (this.lastLatencyAtMs || this.flowStartMs || atMs);
    const stepMs = Math.max(0, Math.round(atMs - stepBase));

    if (phase === 'payment') {
      this.paymentCumulativeMs += stepMs;
    } else {
      this.consultCumulativeMs += stepMs;
    }

    const entry: LatencyEntry = {
      label,
      atMs,
      stepMs,
      cumulativeMs: phase === 'payment' ? this.paymentCumulativeMs : this.consultCumulativeMs,
      phase,
    };
    this.latencyEntries = [...this.latencyEntries, entry];
    this.lastLatencyAtMs = atMs;
    console.log(
      `[saved-card][latency][${phase}] ${label}: step=${stepMs}ms cumulative=${entry.cumulativeMs}ms`,
    );
    setTimeout(() => this.cdr.detectChanges());
  }

  private resetLatency(): void {
    this.latencyEntries = [];
    this.lastLatencyAtMs = 0;
    this.consultCumulativeMs = 0;
    this.paymentCumulativeMs = 0;
    this.authIframeLoadRecorded = false;
    this.authChallengeSeen = false;
    this.authChallengeSent = false;
  }

  private startOrderIdInterval(): void {
    if (this.orderIdInterval) {
      return;
    }
    this.orderIdInterval = setInterval(() => {
      if (!this.showRegisterIframe && !this.showAuthOverlay) {
        this.myForm.get('orderNumber')?.setValue(this.savedCardService.createOrderNumber(), { emitEvent: false });
      }
    }, 1000);
  }

  private stopOrderIdInterval(): void {
    if (this.orderIdInterval) {
      clearInterval(this.orderIdInterval);
      this.orderIdInterval = null;
    }
  }
}
